import { Event, ServerEvent, TServerSimpleCalendarEventList } from 'types/calendar';
import { RRule, datetime } from 'rrule';
import dayjs from 'dayjs';
import { DEFAULT_CALENDAR_COLOR } from 'constants/common';
export const transferGoogleEventToServerEvent = (data: Event): ServerEvent => {
    return {
        start: data.start.dateTime,
        end: data.end.dateTime,
        eventId: data.id,
        colorId: data.channelId,
        description: data.description,
        email: data.attendees?.map((item) => item.email),
        location: data.location,
        summary: data.summary,
        timeZone: data.start.timeZone,
        providerId: data.providerId,
        channelId: data.channelId,
        serviceId: data.serviceId,
        duration: data.duration,
        meetingLink: data.meetingLink,
        patientName: data.patientName,
        patientPhone: data.patientPhone,
        sendForm: data.sendForm,
    };
};

export const transferServerEventToGoogleEvent = (data: ServerEvent): Event => {
    return {
        id: data.eventId,
        summary: data.summary,
        start: {
            timeZone: data.timeZone,
            dateTime: data.start,
        },
        end: {
            timeZone: data.timeZone,
            dateTime: data.end,
        },
        attendees: data.email?.map((email) => {
            return {
                email,
                displayName: email,
            };
        }),
        providerId: data.providerId,
        channelId: data.channelId,
        serviceId: data.serviceId,
        duration: data.duration,
        meetingLink: data.meetingLink,
        sendForm: data.sendForm,
        patientName: data.patientName,
        patientPhone: data.patientPhone,
        location: data.location,
        colorId: data.colorId,
        description: data.description,
    };
};

export const transferServiceSimpleListToGoogleEvent = (data: TServerSimpleCalendarEventList, start: string, end: string): Event[] => {
    const ret: Event[] = [];
    data?.appointmentList?.forEach((item) => {
        const event: Event = {
            id: item.id + '',
            summary: item.title,
            start: {
                timeZone: 'UTC',
                dateTime: item.startAt,
            },
            end: {
                timeZone: 'UTC',
                dateTime: item.endAt,
            },
            attendees: [],
            description: '',
            type: 'appointment',
            appointmentStatus: item.appointmentStatus || undefined,
            color: item.calendarColor || DEFAULT_CALENDAR_COLOR,
            scheduledBy: item.scheduledBy,
            serviceSpecialty: item.serviceSpecialty,
            clientId: item.clientId,
            isSample: item.isSample,
        }
        ret.push(event);
    })
    data?.googleList?.forEach((item) => {
        if (!item.recurrenceRule) {
            const event: Event = {
                id: item.id + '',
                summary: item.title,
                start: {
                    timeZone: 'UTC',
                    dateTime: item.startAt,
                },
                end: {
                    timeZone: 'UTC',
                    dateTime: item.endAt,
                },
                attendees: [],
                description: '',
                type: 'google',
                appointmentStatus: item.appointmentStatus || undefined,
                color: item.calendarColor || DEFAULT_CALENDAR_COLOR,
                scheduledBy: item.scheduledBy,
                serviceSpecialty: item.serviceSpecialty,
            }
            ret.push(event);
        } else {
            if (item.recurrenceRule && item.recurrenceRule.includes('UNTIL=')) {
                return;
            }
            const startDay = new Date(start);
            const endDay = new Date(end);
            let ruleString = item.recurrenceRule;
            let exdateString: string | undefined = '';
            let dtstartString: string | undefined = '';
            let exdate: dayjs.Dayjs | undefined = undefined;
            if (ruleString.includes('\n')) {
                const ruleArr = ruleString.split('\n');
                exdateString = ruleArr.find(line => line.startsWith('EXDATE'));
                dtstartString = ruleArr.find(line => line.startsWith('DTSTART'));
                for (let i = 0; i < ruleArr.length; i++) {
                    const ruleItem = ruleArr[i];
                    if (ruleItem.startsWith('RRULE:')) {
                        ruleString = ruleItem;
                        break;
                    }
                }
            }
            if (!ruleString.startsWith('RRULE:')) {
                return;
            }
            if (ruleString.includes("FREQ=YEARLY")) {
                const event: Event = {
                    id: item.id + '',
                    summary: item.title,
                    start: {
                        timeZone: 'UTC',
                        dateTime: dayjs(item.startAt).year(dayjs().year()).toISOString(),
                    },
                    end: {
                        timeZone: 'UTC',
                        dateTime: dayjs(item.endAt).year(dayjs().year()).toISOString(),
                    },
                    attendees: [],
                    description: '',
                    type: 'google',
                    appointmentStatus: item.appointmentStatus || undefined,
                    color: item.calendarColor || DEFAULT_CALENDAR_COLOR,
                    scheduledBy: item.scheduledBy,
                    serviceSpecialty: item.serviceSpecialty,
                }
                ret.push(event);
                return;
            }

            const parsed = RRule.fromString(ruleString);
            let dtstart = new Date(item.startAt);
            if (exdateString) {
                const exdateStringArr = exdateString.substring(exdateString.indexOf('=') + 1).split(':');
                if (exdateStringArr && exdateStringArr.length === 2) {
                    try {
                        exdate = dayjs.tz(exdateStringArr[1], exdateStringArr[0]);
                    } catch (e) {
                        console.error('Error parsing EXDATE:', e);
                    }
                }
            }
            if (dtstartString) {
                const dtstartStringArr = dtstartString.substring(dtstartString.indexOf('=') + 1).split(':');
                if (dtstartStringArr && dtstartStringArr.length === 2) {
                    dtstart = dayjs.tz(dtstartStringArr[1], dtstartStringArr[0]).toDate();
                }
            }
            const rule = new RRule({
                ...parsed.origOptions,
                dtstart: dtstart
            });

            const dates = rule.between(datetime(startDay.getFullYear(), startDay.getMonth() + 1, startDay.getDate()), datetime(endDay.getFullYear(), endDay.getMonth() + 1, endDay.getDate()))

            dates.forEach((date) => {
                const duration = new Date(item.endAt).getTime() - new Date(item.startAt).getTime();
                const startTime = dayjs(date).hour(dayjs(item.startAt).hour()).minute(dayjs(item.startAt).minute()).second(0);
                if (exdate) {
                    if (exdate.format('YYYY-MM-DD') === startTime.format('YYYY-MM-DD')) {
                        return;
                    }
                }
                const endTime = dayjs(startTime).add(duration, 'ms');
                const event: Event = {
                    id: item.id + '',
                    summary: item.title,
                    start: {
                        timeZone: 'UTC',
                        dateTime: startTime.toISOString(),
                    },
                    end: {
                        timeZone: 'UTC',
                        dateTime: endTime.toISOString(),
                    },
                    attendees: [],
                    description: '',
                    type: 'google',
                    appointmentStatus: item.appointmentStatus || undefined,
                    color: item.calendarColor || DEFAULT_CALENDAR_COLOR,
                    scheduledBy: item.scheduledBy,
                    serviceSpecialty: item.serviceSpecialty,
                }
                ret.push(event);
            })
        }
    })

    data?.blockerList?.forEach((item) => {
        if (!item.recurrenceRule) {
            const event: Event = {
                id: item.id + '',
                summary: item.title,
                start: {
                    timeZone: 'UTC',
                    dateTime: item.startAt,
                },
                end: {
                    timeZone: 'UTC',
                    dateTime: item.endAt,
                },
                attendees: [],
                description: '',
                type: 'blocker',
                appointmentStatus: item.appointmentStatus || undefined,
                scheduledBy: item.scheduledBy,
                serviceSpecialty: item.serviceSpecialty,
                isSample: item.isSample,
            }
            ret.push(event);
        } else {
            if (item.recurrenceRule && !item.recurrenceRule.includes('UNTIL=')) {
                const startDay = new Date(start);
                const endDay = new Date(end);

                // const startDay = new Date("2025-03-29");
                // const endDay = new Date("2025-04-12")
                // const rule = RRule.fromString('RRULE:FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR');
                // rule.options.dtstart = new Date(item.startAt);
                // const dates = rule.between(datetime(startDay.getFullYear(), startDay.getMonth() + 1, startDay.getDate()), datetime(endDay.getFullYear(), endDay.getMonth() + 1, endDay.getDate()))

                const parsed = RRule.fromString(item.recurrenceRule);
                let dtstart = new Date(item.startAt);
                const rule = new RRule({
                    ...parsed.origOptions,
                    dtstart: dtstart
                });
                const dates = rule.between(datetime(startDay.getFullYear(), startDay.getMonth() + 1, startDay.getDate()), datetime(endDay.getFullYear(), endDay.getMonth() + 1, endDay.getDate()))

                dates.forEach((date) => {
                    const duration = new Date(item.endAt).getTime() - new Date(item.startAt).getTime();
                    const startTime = dayjs(date).hour(dayjs(item.startAt).hour()).minute(dayjs(item.startAt).minute()).second(0);
                    //todo if in diff timezone, dates will be diff
                    // const timezone = 'America/New_York';
                    // const startTimeInTimezone = startTime.tz(timezone);

                    // // 提取日期部分进行比较
                    // const dateOnly1 = dayjs(startTime.format('YYYY-MM-DD HH:mm:ss')).hour(0).minute(0).second(0);
                    // const dateOnly2 = dayjs(startTimeInTimezone.format('YYYY-MM-DD HH:mm:ss')).hour(0).minute(0).second(0);

                    // const differenceInSecond = dateOnly1.diff(dateOnly2, 'seconds');
                    // let differenceInDays = 0;
                    // if (differenceInSecond === 86400) {
                    //     differenceInDays = 1;
                    //     startTime.add(-1, 'day');
                    // } else if (differenceInSecond === -86400) {
                    //     differenceInDays = -1;
                    //     startTime.add(1, 'day');
                    // }

                    const endTime = dayjs(startTime).add(duration, 'ms');
                    const event: Event = {
                        id: item.id + '',
                        summary: item.title,
                        start: {
                            timeZone: 'UTC',
                            dateTime: startTime.toISOString(),
                        },
                        end: {
                            timeZone: 'UTC',
                            dateTime: endTime.toISOString(),
                        },
                        attendees: [],
                        description: '',
                        type: 'blocker',
                        appointmentStatus: item.appointmentStatus || undefined,
                        scheduledBy: item.scheduledBy,
                        serviceSpecialty: item.serviceSpecialty,
                    }
                    ret.push(event);
                })
            }
        }
    })
    return ret;
}


export const EventData = {
    transferGoogleEventToServerEvent,
    transferServerEventToGoogleEvent,
    transferServiceSimpleListToGoogleEvent,
};
